import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Plus,
  Clock,
  HandCoins,
  X,
  FileText,
  Lock,
  Edit2,
  Trash2,
  Check,
  BellOff,
} from 'lucide-react';
import { formatMoney } from '../../lib/i18n';
import { StorageService } from '../../lib/storage';
import { getLocalDateString } from '../../lib/dateUtils';

interface CustomEvent {
  id: string;
  title: string;
  date: string;
}

export const CalendarView: React.FC = () => {
  const { debts, transactions, notes, updateNote, setCurrentPage, currency, language, triggerConfetti, t } = useApp();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<string>(getLocalDateString());

  // Persisted Custom Events
  const [customEvents, setCustomEvents] = useState<CustomEvent[]>(() => {
    return StorageService.getItem('mylife_calendar_custom_events', []);
  });

  useEffect(() => {
    StorageService.setItem('mylife_calendar_custom_events', customEvents);
  }, [customEvents]);

  // Add Event Modal
  const [newEventTitle, setNewEventTitle] = useState('');
  const [isAddEventOpen, setIsAddEventOpen] = useState(false);

  // Edit Event Modal
  const [isEditEventOpen, setIsEditEventOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CustomEvent | null>(null);
  const [editEventTitle, setEditEventTitle] = useState('');
  const [editEventDate, setEditEventDate] = useState('');

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsAddEventOpen(false);
        setIsEditEventOpen(false);
      }
    };
    if (isAddEventOpen || isEditEventOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isAddEventOpen, isEditEventOpen]);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // First day and total days of month
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const handlePrevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  // Find all events for a given day (YYYY-MM-DD) - Excluding Tasks
  const getEventsForDate = (dateStr: string) => {
    const dayDebts = debts.filter((d) => d.dueDate === dateStr && d.status === 'pending');
    const dayTransactions = transactions.filter((tx) => tx.date === dateStr);
    const dayNotes = notes.filter((n) => !n.isTrashed && n.reminder && n.reminder === dateStr);
    const dayCustom = customEvents.filter((e) => e.date === dateStr);

    return {
      debts: dayDebts,
      transactions: dayTransactions,
      notes: dayNotes,
      custom: dayCustom,
      totalCount:
        dayDebts.length +
        dayTransactions.length +
        dayNotes.length +
        dayCustom.length,
    };
  };

  const selectedEvents = getEventsForDate(selectedDate);

  // Add Custom Event
  const handleAddCustomEvent = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEventTitle.trim()) return;

    const newEv: CustomEvent = {
      id: 'ev_' + Date.now(),
      title: newEventTitle.trim(),
      date: selectedDate,
    };

    setCustomEvents((prev) => [...prev, newEv]);
    setNewEventTitle('');
    setIsAddEventOpen(false);
    triggerConfetti();
  };

  // Open Edit Event Modal
  const handleOpenEdit = (ev: CustomEvent) => {
    setEditingEvent(ev);
    setEditEventTitle(ev.title);
    setEditEventDate(ev.date);
    setIsEditEventOpen(true);
  };

  // Save Edit Event
  const handleSaveEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingEvent || !editEventTitle.trim()) return;

    setCustomEvents((prev) =>
      prev.map((ev) =>
        ev.id === editingEvent.id
          ? { ...ev, title: editEventTitle.trim(), date: editEventDate || ev.date }
          : ev
      )
    );

    setIsEditEventOpen(false);
    setEditingEvent(null);
    triggerConfetti();
  };

  // Delete Custom Event
  const handleDeleteCustomEvent = (id: string) => {
    setCustomEvents((prev) => prev.filter((ev) => ev.id !== id));
  };

  // Remove Note Reminder from Note
  const handleRemoveNoteReminder = (noteId: string) => {
    updateNote(noteId, { reminder: undefined });
  };

  return (
    <div className="space-y-6 animate-fade-in pb-16">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
            <CalendarDays className="w-6 h-6 text-emerald-500" />
            <span>Calendar & Schedule</span>
          </h1>
        </div>

        {/* Month Navigation */}
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrevMonth}
            className="p-2 border border-slate-200 dark:border-slate-800 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm font-bold text-slate-900 dark:text-white min-w-[140px] text-center">
            {monthNames[month]} {year}
          </span>
          <button
            onClick={handleNextMonth}
            className="p-2 border border-slate-200 dark:border-slate-800 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Main Calendar Grid & Schedule Details */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Calendar Matrix (8 cols) */}
        <div className="lg:col-span-8 bg-white dark:bg-[#111827] border border-slate-200/80 dark:border-slate-800 rounded-3xl p-5 shadow-xs space-y-3">
          {/* Weekday Names */}
          <div className="grid grid-cols-7 gap-2 text-center text-xs font-bold text-slate-400">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
              <div key={d} className="py-1">
                {d}
              </div>
            ))}
          </div>

          {/* Days Matrix */}
          <div className="grid grid-cols-7 gap-2">
            {/* Empty slots before first day */}
            {Array.from({ length: firstDayOfMonth }).map((_, i) => (
              <div key={`empty-${i}`} className="h-14 sm:h-20 rounded-2xl bg-slate-50/50 dark:bg-slate-900/30" />
            ))}

            {/* Month Days */}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const dayNum = i + 1;
              const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
              const isSelected = selectedDate === dateStr;
              const isToday = getLocalDateString() === dateStr;
              const dayEvents = getEventsForDate(dateStr);
              const hasEvents = dayEvents.totalCount > 0;

              return (
                <button
                  key={dateStr}
                  onClick={() => setSelectedDate(dateStr)}
                  className={`h-14 sm:h-20 p-2 rounded-2xl border text-left transition-all flex flex-col justify-between ${
                    isSelected
                      ? 'border-emerald-500 bg-emerald-500/10 shadow-xs ring-2 ring-emerald-500/20'
                      : isToday
                      ? 'border-emerald-400/50 bg-slate-50 dark:bg-slate-800/80'
                      : 'border-slate-100 dark:border-slate-800/80 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                  }`}
                >
                  <span
                    className={`text-xs font-bold inline-flex items-center justify-center w-6 h-6 rounded-full ${
                      isToday
                        ? 'bg-emerald-600 text-white shadow-xs'
                        : 'text-slate-800 dark:text-slate-200'
                    }`}
                  >
                    {dayNum}
                  </span>

                  {/* Indicator dots */}
                  {hasEvents && (
                    <div className="flex items-center gap-1 overflow-hidden flex-wrap">
                      {dayEvents.debts.length > 0 && <span className="w-1.5 h-1.5 rounded-full bg-amber-500" title="Debts" />}
                      {dayEvents.notes.length > 0 && <span className="w-1.5 h-1.5 rounded-full bg-purple-500" title="Notes Reminder" />}
                      {dayEvents.custom.length > 0 && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" title="Events" />}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected Day Details Panel (4 cols) */}
        <div className="lg:col-span-4 bg-white dark:bg-[#111827] border border-slate-200/80 dark:border-slate-800 rounded-3xl p-6 shadow-xs space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
            <div>
              <h3 className="text-sm font-bold text-slate-900 dark:text-white">Schedule Details</h3>
              <p className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold">{selectedDate}</p>
            </div>
            <button
              onClick={() => setIsAddEventOpen(true)}
              className="p-1.5 text-emerald-600 hover:bg-emerald-500/10 rounded-xl transition-colors"
              title="Add new event for this date"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          {selectedEvents.totalCount === 0 ? (
            <div className="py-8 text-center text-slate-400 text-xs">
              No bills, note reminders, or events scheduled for this day.
            </div>
          ) : (
            <div className="space-y-3">
              {/* Custom Reminders (With Edit & Delete Options!) */}
              {selectedEvents.custom.map((ev) => (
                <div
                  key={ev.id}
                  className="p-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-start justify-between gap-2.5 group hover:bg-emerald-500/15 transition-all"
                >
                  <div className="flex items-start gap-2.5 min-w-0 flex-1">
                    <Clock className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <div className="text-xs font-bold text-slate-900 dark:text-white break-words">
                      {ev.title}
                    </div>
                  </div>

                  {/* Actions: Edit & Delete */}
                  <div className="flex items-center gap-1 opacity-90 group-hover:opacity-100">
                    <button
                      onClick={() => handleOpenEdit(ev)}
                      className="p-1 text-slate-400 hover:text-emerald-600 hover:bg-emerald-500/20 rounded-lg transition-colors"
                      title="Edit event"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDeleteCustomEvent(ev.id)}
                      className="p-1 text-slate-400 hover:text-rose-500 hover:bg-rose-500/20 rounded-lg transition-colors"
                      title="Delete event"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}

              {/* Note Reminders */}
              {selectedEvents.notes.map((n) => (
                <div
                  key={n.id}
                  className="p-3 rounded-2xl bg-purple-500/10 border border-purple-500/20 flex items-start justify-between gap-2.5 group hover:bg-purple-500/15 transition-all"
                >
                  <div
                    onClick={() => setCurrentPage('notes')}
                    className="flex items-start gap-2.5 min-w-0 flex-1 cursor-pointer"
                  >
                    {n.isLocked ? (
                      <Lock className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    ) : (
                      <FileText className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-bold text-slate-900 dark:text-white truncate">
                        {n.title}
                      </div>
                      <span className="text-[10px] text-purple-600 dark:text-purple-400 font-semibold">
                        Note Reminder • Click to open
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={() => handleRemoveNoteReminder(n.id)}
                    className="p-1 text-slate-400 hover:text-rose-500 hover:bg-rose-500/20 rounded-lg transition-colors"
                    title="Remove reminder from calendar"
                  >
                    <BellOff className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}

              {/* Debts due */}
              {selectedEvents.debts.map((d) => (
                <div
                  key={d.id}
                  className="p-3 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-start gap-2.5"
                >
                  <HandCoins className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-xs font-bold text-slate-900 dark:text-white">
                      {d.type === 'owed' ? 'Receivable from: ' : 'Payable to: '} {d.personName}
                    </div>
                    <span className="text-[10px] text-amber-600 dark:text-amber-400 font-bold">
                      {formatMoney(d.amount, currency, language)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Custom Event Modal */}
      {isAddEventOpen && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsAddEventOpen(false);
          }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
        >
          <div className="w-full max-w-md bg-white dark:bg-[#111827] border border-slate-200 dark:border-slate-800 rounded-3xl p-6 shadow-2xl space-y-4 animate-scale-in">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-slate-900 dark:text-white">নতুন শিডিউল ইভেন্ট যোগ করুন</h3>
              <button
                onClick={() => setIsAddEventOpen(false)}
                className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAddCustomEvent} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">ইভেন্ট / রিমাইন্ডার নাম</label>
                <input
                  type="text"
                  placeholder="যেমন: গাড়ির পলিসি রিনিউ, মিটিং, অ্যাপয়েন্টমেন্ট"
                  value={newEventTitle}
                  onChange={(e) => setNewEventTitle(e.target.value)}
                  required
                  autoFocus
                  className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">তারিখ</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-full px-3.5 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-white focus:outline-none"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsAddEventOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-100 rounded-xl"
                >
                  বাতিল
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-md shadow-emerald-500/25 flex items-center gap-1.5"
                >
                  <Check className="w-4 h-4" />
                  <span>ইভেন্ট সেভ করুন</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Custom Event Modal */}
      {isEditEventOpen && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsEditEventOpen(false);
          }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
        >
          <div className="w-full max-w-md bg-white dark:bg-[#111827] border border-slate-200 dark:border-slate-800 rounded-3xl p-6 shadow-2xl space-y-4 animate-scale-in">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-slate-900 dark:text-white">ইভেন্ট এডিট করুন</h3>
              <button
                onClick={() => setIsEditEventOpen(false)}
                className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveEdit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">ইভেন্ট / রিমাইন্ডার নাম</label>
                <input
                  type="text"
                  value={editEventTitle}
                  onChange={(e) => setEditEventTitle(e.target.value)}
                  required
                  autoFocus
                  className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">তারিখ</label>
                <input
                  type="date"
                  value={editEventDate}
                  onChange={(e) => setEditEventDate(e.target.value)}
                  className="w-full px-3.5 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-white focus:outline-none"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsEditEventOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-100 rounded-xl"
                >
                  বাতিল
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-md shadow-emerald-500/25 flex items-center gap-1.5"
                >
                  <Check className="w-4 h-4" />
                  <span>আপডেট করুন</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
